import type { v4, Manifest } from '@sap/ux-specification-types';
import { descriptionSrcURL, syncRule, validity } from '../../../common/decoration/decorators';
import {
    getCustomFilterFieldFieldPath,
    getFilterFieldPath,
    getSelectionFieldsPath,
    getSettingsPathForPage
} from '../../application';

/**
 * Updates the manifest section with the value list from the configuration object.
 *
 * @param manifestSection - The section of the manifest to be updated
 * @param configObject - The configuration object containing the data
 * @param key - The key in the configuration object to retrieve the value
 * @param manifestKey - The key in the manifest section to update (optional)
 */
function exportVisualFilterValueList(
    manifestSection: unknown,
    configObject: unknown,
    key: string,
    manifestKey?: string
): void {
    if (manifestSection[manifestKey]) {
        manifestSection[manifestKey].valueList = configObject[key];
    } else {
        manifestSection[manifestKey] = { valueList: configObject[key] };
    }
}

export class FilterBar implements v4.FilterBar {
    selectionFields?: v4.SelectionFields | v4.CompactFilters;

    visualFilters?: v4.VisualFilters;

    @syncRule({
        manifest: {
            path: (pageName: string[]): string => getSettingsPathForPage(pageName)
        }
    })
    @validity({
        since: '1.99.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/4bd7590569c74c61a0124c6e370030f6')
    hideFilterBar?: boolean;

    @syncRule({
        manifest: {
            path: getSelectionFieldsPath
        }
    })
    @validity({
        since: '1.93.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/33f3d807c10b47d9a8141692d2619dc2')
    initialLayout?: v4.InitialLayoutType;

    @syncRule({
        manifest: {
            path: getSelectionFieldsPath
        }
    })
    @validity({
        since: '1.93.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/33f3d807c10b47d9a8141692d2619dc2')
    layout?: v4.LayoutType;
}

export class VisualFilter implements v4.VisualFilter {
    @syncRule({
        manifest: {
            path: getFilterFieldPath
        }
    })
    @validity({
        since: '1.93.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/33f3d807c10b47d9a8141692d2619dc2')
    availability?: v4.Availability;

    @syncRule({
        manifest: {
            path: getFilterFieldPath,
            import: (manifestSection: unknown) => manifestSection?.['visualFilter']?.['valueList'],
            export: exportVisualFilterValueList,
            key: 'visualFilter'
        }
    })
    @validity({
        since: '1.93.0'
    })
    visualFilterValueList?: string;
}

export class CustomFilterFieldPosition implements v4.CustomFilterFieldPosition {
    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId: string, columnKey: string): string =>
                getCustomFilterFieldFieldPath(pageName, manifest, sectionId, columnKey) + '/position'
        }
    })
    @validity({
        since: '1.84.0'
    })
    anchor?: string;

    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId: string, columnKey: string): string =>
                getCustomFilterFieldFieldPath(pageName, manifest, sectionId, columnKey) + '/position'
        }
    })
    @validity({
        since: '1.84.0'
    })
    placement: v4.FilterFieldPlacement;
}

export class CustomFilterField implements v4.CustomFilterField {
    @syncRule({
        manifest: {
            path: getCustomFilterFieldFieldPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    label: string;

    @syncRule({
        manifest: {
            path: getCustomFilterFieldFieldPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    property: string;

    @syncRule({
        manifest: {
            path: getCustomFilterFieldFieldPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    template: string;

    @syncRule({
        manifest: {
            path: getCustomFilterFieldFieldPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    required: boolean;

    @syncRule({
        manifest: {
            path: getCustomFilterFieldFieldPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    position: CustomFilterFieldPosition;
}
